import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import './tailwind.css';
import { FaUserPlus, FaEnvelope, FaLock, FaBuilding, FaSave, FaTimes } from 'react-icons/fa';

export default function CreaAgente() {
  const navigate = useNavigate();

  const CREATE_AGENT_USER_ENDPOINT = 'https://zwgpmobugscpdcavajbu.supabase.co/functions/v1/create-agent-user';

  const [userName, setUserName] = useState('');
  const [userSurname, setUserSurname] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [agencies, setAgencies] = useState([]);

  // Nuovi stati per il ruolo dell'utente e l'ID dell'agenzia (se applicabile)
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUserAgencyId, setCurrentUserAgencyId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setFeedbackMessage('');
      setIsError(false);

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;

      if (sessionError || !userId) {
        console.error('Nessuna sessione utente o errore:', sessionError?.message);
        navigate('/'); // Reindirizza al login
        return;
      }

      // 1. Recupera i dati del profilo dell'utente loggato (ruolo e agenzia_id)
      const { data: utente, error: userProfileError } = await supabase
        .from('utenti')
        .select('ruolo, agenzia_id') // Assumi che 'agenzia_id' sia il campo che collega l'utente all'agenzia
        .eq('id', userId)
        .single();

      if (userProfileError || !utente) {
        console.error('Errore nel caricamento del profilo utente:', userProfileError?.message);
        setFeedbackMessage('Errore nel caricamento del profilo utente.');
        setIsError(true);
        setLoading(false);
        navigate('/');
        return;
      }

      setCurrentUserRole(utente.ruolo);
      setCurrentUserAgencyId(utente.agenzia_id); // Imposta l'ID dell'agenzia dell'utente

      // 2. Logica per caricare le agenzie (o impostare quella predefinita)
      if (utente.ruolo === 'broker') {
        // Se è un broker, carica tutte le agenzie per il dropdown
        const { data, error } = await supabase
          .from('agenzie')
          .select('id, nome')
          .order('nome', { ascending: true });

        if (error) {
          console.error("Error fetching agencies:", error.message);
          setFeedbackMessage('Errore nel caricamento delle agenzie.');
          setIsError(true);
        } else {
          setAgencies(data || []);
          if (data && data.length > 0) {
            setSelectedAgency(data[0].id); // Pre-seleziona la prima agenzia per il broker
          }
        }
      } else if (utente.ruolo === 'agenzia') {
        // Se è un'agenzia, blocca la selezione sull'agenzia dell'utente
        if (utente.agenzia_id) {
          setSelectedAgency(utente.agenzia_id); // Imposta l'agenzia di appartenenza
          // Opzionale: puoi anche caricare il nome dell'agenzia per mostrarlo a video
          const { data: agencyData, error: agencyError } = await supabase
            .from('agenzie')
            .select('id, nome')
            .eq('id', utente.agenzia_id)
            .single();

          if (agencyError || !agencyData) {
            console.error("Error fetching current agency data:", agencyError?.message);
            setFeedbackMessage('Errore nel caricamento dei dati della tua agenzia.');
            setIsError(true);
            setAgencies([]); // Nessuna agenzia disponibile per la selezione
          } else {
            setAgencies([agencyData]); // Aggiungi solo l'agenzia corrente all'elenco
          }
        } else {
          setFeedbackMessage('Errore: La tua agenzia non è stata trovata.');
          setIsError(true);
        }
      } else {
        // Se il ruolo non è né broker né agenzia, reindirizza (o mostra errore)
        console.warn(`Utente con ruolo ${utente.ruolo} ha tentato di accedere a CreaAgente.`);
        navigate('/dashboard'); // O una pagina di errore
        setLoading(false);
        return;
      }
      setLoading(false);
    };

    fetchData();
  }, [navigate]); // Aggiungi navigate alle dipendenze

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedbackMessage('');
    setIsError(false);

    if (!userName || !userSurname || !userEmail || !userPassword || !selectedAgency) {
      setFeedbackMessage('Tutti i campi sono obbligatori.');
      setIsError(true);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(CREATE_AGENT_USER_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session.access_token}`
        },
        body: JSON.stringify({
          userName,
          userSurname,
          userEmail,
          userPassword,
          agencyId: selectedAgency,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      setFeedbackMessage('Account creato con successo!');
      setIsError(false);
      setUserName('');
      setUserSurname('');
      setUserEmail('');
      setUserPassword('');
      // Non resettare selectedAgency per l'agenzia, ma potresti volerlo fare per il broker.
      // Per il momento lo lascio così, ma puoi aggiungere una logica condizionale qui.

    } catch (error) {
      console.error('Error during agent creation:', error.message);
      setFeedbackMessage(`Errore: ${error.message}`);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !currentUserRole) { // Mostra il caricamento iniziale solo se il ruolo non è ancora stato determinato
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-slate-800">
        <p className="text-xl font-semibold">Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-2xl lg:max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-bold uppercase mb-4 text-center text-mioblu">
          Crea Nuovo Account
        </h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ... Campi Nome, Cognome, Email, Password ... (restano invariati) */}
          <div>
            <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
              Nome Account <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <FaUserPlus className="text-gray-400" />
              </span>
              <input
                type="text"
                id="userName"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                placeholder="Nome dell'Account"
              />
            </div>
          </div>

          <div>
            <label htmlFor="userSurname" className="block text-sm font-medium text-gray-700 mb-1">
              Cognome Account <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <FaUserPlus className="text-gray-400" />
              </span>
              <input
                type="text"
                id="userSurname"
                value={userSurname}
                onChange={(e) => setUserSurname(e.target.value)}
                required
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                placeholder="Cognome dell'Account"
              />
            </div>
          </div>

          <div>
            <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 mb-1">
              Email Account <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <FaEnvelope className="text-gray-400" />
              </span>
              <input
                type="email"
                id="userEmail"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                required
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                placeholder="email@example.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="userPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Password Temporanea <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <FaLock className="text-gray-400" />
              </span>
              <input
                type="password"
                id="userPassword"
                value={userPassword}
                onChange={(e) => setUserPassword(e.target.value)}
                required
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                placeholder="Password temporanea"
              />
            </div>
          </div>

          {/* Selezione Agenzia - Condizionale per Broker o Agenzia */}
          <div>
            <label htmlFor="agencySelect" className="block text-sm font-medium text-gray-700 mb-1">
              Seleziona Sede <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <FaBuilding className="text-gray-400" />
              </span>
              {currentUserRole === 'broker' ? (
                // Se è un Broker, mostra il dropdown completo
                <select
                  id="agencySelect"
                  value={selectedAgency}
                  onChange={(e) => setSelectedAgency(e.target.value)}
                  required
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                >
                  <option value="">Seleziona una sede</option>
                  {agencies.map((agency) => (
                    <option key={agency.id} value={agency.id}>
                      {agency.nome}
                    </option>
                  ))}
                </select>
              ) : (
                // Se è un'Agenzia, mostra un campo di testo disabilitato con il nome dell'agenzia
                <input
                  type="text"
                  id="agencySelect"
                  value={agencies.length > 0 ? agencies[0].nome : 'Caricamento...'} // Mostra il nome dell'agenzia
                  readOnly // Rende il campo non modificabile
                  disabled // Disabilita l'interazione
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 cursor-not-allowed sm:text-sm"
                />
              )}
            </div>
          </div>

          {/* Feedback Message */}
          {feedbackMessage && (
            <div
              className={`p-3 rounded-md text-sm ${
                isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}
              role="alert"
            >
              {feedbackMessage}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <FaTimes className="mr-2 -ml-1 h-5 w-5" /> Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-miorosso hover:bg-miorossohover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                'Creazione...'
              ) : (
                <>
                  <FaSave className="mr-2 -ml-1 h-5 w-5" /> Crea Account
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}